import React, { useCallback, useEffect, useRef } from 'react';
import { Context } from '../App';
import { Button } from '@/components/ui/button';
import mapboxgl, { Marker } from 'mapbox-gl'; // or "const mapboxgl = require('mapbox-gl');"
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Box, Grid, GridItem, Spacer } from '@chakra-ui/react';
import useModel from './hooks/useModel';
import { getWorksiteAddress } from '@/model/worksite';
import { geocodeAddress } from '@/api/company-setting-controller';
import { SPACING } from '@/generic/config';
import Text from './Text';
import ModelInput from './ModelInput';
import useDialog from './hooks/useDialog';
import Worksite from '@/model/worksite';

// return type of useModel<Worksite>(WorksiteModel) for model
type WorksiteMapComponentProps = {
    model: ReturnType<typeof useModel<Worksite>>["model"];
};

const WorksiteMapComponent = ({ model }: WorksiteMapComponentProps) => {
    const { translations, companySettings } = React.useContext(Context);
    const { openDialog } = useDialog();
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<mapboxgl.Map | null>(null);
    const markers = useRef<Marker[]>([]);
    const [featureChecksum, setFeatureChecksum] = React.useState<number>(0);
    const featuresref = useRef<{ lat: number, lon: number }[]>(JSON.parse(model.object.extraGeoData || "[]"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const createMapMarkerAndFly = useCallback((lat: number, lon: number, map: ReturnType<typeof useRef<mapboxgl.Map | null>>, updateModel: boolean) => {
        if (!map.current || !lat || !lon) return; // Map or model not initialized

        const updateModelCoordinates = (lat: number, lon: number) => {
            model.object.latitude = lat;
            model.object.longitude = lon;
            model.setObject({ ...model.object });
        }

        if (updateModel) {
            updateModelCoordinates(lat, lon);
        }

        markers.current.forEach(marker => {
            marker.remove();
        });
        const marker = new Marker({ color: 'red', draggable: true }).setLngLat([lon, lat]).addTo(map.current!);
        markers.current.push(marker);
        map.current?.flyTo({ center: { lat, lon }, zoom: 17 });
        marker.on('dragend', () => {
            const lngLat = marker.getLngLat();
            updateModelCoordinates(lngLat.lat, lngLat.lng);
        });
    }, [model, markers]);

    useEffect(() => createMapMarkerAndFly(model.object.latitude, model.object.longitude, map, false), [model.object.latitude, model.object.longitude, createMapMarkerAndFly]);


    const Draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
            point: true,
            trash: true
        },
        styles: [
            {
                'id': 'active-points',
                'type': 'circle',
                'filter': ['all',
                    ['==', '$type', 'Point'],
                    ['==', 'meta', 'feature'],
                    ['==', 'active', 'true']],
                'paint': {
                    'circle-radius': 7,
                    'circle-color': 'white',
                    'circle-opacity': 0.8,
                    'circle-stroke-width': 8,
                    'circle-stroke-color': 'red',
                    'circle-stroke-opacity': 0.8
                },
            },
            {
                'id': 'inactive-points',
                'type': 'circle',
                'filter': ['all',
                    ['==', '$type', 'Point'],
                    ['==', 'meta', 'feature'],
                    ['==', 'active', 'false']],
                'paint': {
                    'circle-radius': 10,
                    'circle-color': 'white',
                    'circle-opacity': 0.8,
                    'circle-stroke-width': 5,
                    'circle-stroke-color': 'red',
                    'circle-stroke-opacity': 0.8
                },
            },

        ]
    });

    //Sync features from model to map
    useEffect(() => {
        if (!map.current || !model.object.extraGeoData) return;
        const data = JSON.parse(model.object.extraGeoData);
        if (!data || !Array.isArray(data)) return;

        setTimeout(() => {
            data.forEach((d: any) => {
                try {
                    Draw.add({
                        type: 'Feature',
                        properties: { meta: 'feature', active: true },
                        geometry: {
                            type: 'Point',
                            coordinates: [d.lon, d.lat]
                        }
                    });
                } catch (error) {
                    console.error('Error adding feature to map:', error);
                }
            })
        }, 1000);
    }, [model.object.extraGeoData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!companySettings || !companySettings.data) return;
        if (mapContainer != null && mapContainer.current) {
            if (map.current) return; // initialize map only once
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                accessToken: companySettings.data.mapboxToken,
                style: 'mapbox://styles/mapbox/standard-satellite',
                center: [12.9390, 57.72085],
                zoom: 10
            });

            map.current.on('load', () => {
                map.current?.addControl(Draw, 'top-right');
            });

            const events = ['draw.create', 'draw.selectionchange', 'draw.delete', 'draw.update', 'draw.modechange', 'draw.actionable'];
            events.forEach(event => {
                map.current?.on(event, () => {
                    featuresref.current = Draw.getAll().features.filter((f: any) => f.geometry.coordinates.length > 0).map((f: any) => ({
                        lat: parseFloat(f.geometry.coordinates[1].toFixed(10)),
                        lon: parseFloat(f.geometry.coordinates[0].toFixed(10))
                    }));
                    syncAllFeatures();
                    setFeatureChecksum((prev) => prev + 1);
                });
            });
        }
    });

    const syncAllFeatures = () => {
        const data = Draw.getAll().features.filter((f: any) => f.geometry.coordinates.length > 0);
        if (!data) return;
    };

    useEffect(() => {
        if (featuresref.current.length === 0) {
            model.object.extraGeoData = "";
            model.setObject({ ...model.object });
            return;
        }

        model.object.extraGeoData = JSON.stringify(featuresref.current);
        model.setObject({ ...model.object });
        // model should not be in the dependency array!
    }, [featureChecksum]); // eslint-disable-line react-hooks/exhaustive-deps


    if (!Draw) return null;

    const flyToWorksite = async (force: boolean) => {

        if (companySettings && companySettings.data && !companySettings.data.secrets?.geocodingToken?.set) {
            openDialog({
                title: translations.model.companySettings.geoCodingTokenMissing.title,
                body: translations.model.companySettings.geoCodingTokenMissing.description,
                buttons: [
                    {
                        title: translations.common.ok,
                        onClick: () => { }
                    }
                ]
            });
            return;
        }

        if (!force && (model.object.latitude || model.object.longitude)) {
            openDialog({
                title: translations.model.worksite.cards.map.coordinatesAlreadyExist.title,
                body: translations.model.worksite.cards.map.coordinatesAlreadyExist.description,
                buttons: [
                    {
                        title: translations.common.no,
                        onClick: () => { }
                    },
                    {
                        title: translations.common.yes,
                        backgroundColor: "red",
                        onClick: () => flyToWorksite(true)
                    }
                ]
            });
            return;
        }

        // Our backend calls the geocoding service, so the API key never reaches the browser.
        const places = await geocodeAddress(getWorksiteAddress(model.object)).catch(() => null);
        if (places === null) {
            return; // the API error toast already explains it
        }
        const coordinates = places[0];
        if (!coordinates) {
            openDialog({
                title: translations.model.worksite.cards.map.coordinatesNotFound.title,
                body: translations.model.worksite.cards.map.coordinatesNotFound.description,
                buttons: [
                    {
                        title: translations.common.ok,
                        onClick: () => { }
                    }
                ]
            });
            return;
        }
        createMapMarkerAndFly(Number(coordinates.lat), Number(coordinates.lon), map, true);
    };

    return (
        <Grid height="100%" templateColumns="repeat(12, 1fr)">
            <GridItem height="100%" display="flex" flexDir="column" colSpan={4} mr={SPACING}>
                <Text variant="subtitle">{translations.model.worksite.cards.map.customMarkers.description}</Text>
                <Spacer />
                <Box>
                    <ModelInput size="sm" title='Latitude' fieldKey="latitude" model={model} />
                    <ModelInput size="sm" title='Longitude' fieldKey="longitude" model={model} />
                    <Button w="100%" background="#7CB9E8" borderRadius={0} my={SPACING} flexGrow={1} onClick={() => flyToWorksite(false)}>
                        {translations.model.worksite.cards.map.findWorksite}
                    </Button>
                    <Button w="100%" background="#7CB9E8" borderRadius={0} my={SPACING} flexGrow={1} onClick={() => {
                        const mapCenter = map.current?.getCenter();
                        if (mapCenter) {
                            createMapMarkerAndFly(mapCenter.lat, mapCenter.lng, map, true);
                        }
                    }}>
                        {translations.model.worksite.cards.map.setMarkerToCenter}
                    </Button>
                </Box>
            </GridItem>
            <GridItem height="98%" colSpan={8} bg={"red"}>
                <div ref={mapContainer} style={{ height: "100%", width: "100%" }} />
            </GridItem>
        </Grid>
    );
}

export default WorksiteMapComponent;